import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/api";

const AuthContext = createContext();

/**
 * The permission-bearing fields, and the only ones a re-sync overwrites.
 *
 * Everything else in the cached object is left alone on purpose: Profile.jsx
 * merges just its own keys back after a name or avatar edit, so replacing the
 * whole object from the server would undo an edit the user can still see.
 */
const PERMISSION_FIELDS = ["group_names", "is_superuser", "is_staff"];

/**
 * Time-zone fields ride the same re-sync: `timezone` is the user's own override
 * ("" = inherit the company's), `effective_timezone` the zone actually in force.
 * The latter is mirrored to localStorage 'timezone', where utils/locale.js reads it.
 */
const TIMEZONE_FIELDS = ["timezone", "effective_timezone"];

/** Everything a re-sync may overwrite. */
const SYNCED_FIELDS = [...PERMISSION_FIELDS, ...TIMEZONE_FIELDS];

/**
 * Focus fires on every alt-tab. One re-sync per half minute keeps a permission
 * change feeling immediate without a request per window switch.
 */
const RESYNC_MIN_INTERVAL_MS = 30_000;

/** Dispatched by api.js after a silent token refresh returns fresh fields. */
export const USERDETAILS_EVENT = "auth-userdetails";

/**
 * True when `fresh` says nothing new about `prev`.
 *
 * A field absent from `fresh` is "no news", not "now empty" — an older backend
 * that does not send it must never be read as a permission being revoked. An
 * explicit `[]` is news, though: being removed from every group is a real state,
 * and it is this bug in reverse.
 *
 * group_names is compared as a SET. Neither the serializer nor the login
 * payload orders it, so the same groups can come back in a different order; a
 * positional compare would call that a change and re-render every consumer on
 * each sync.
 */
const samePermissions = (prev, fresh) =>
  SYNCED_FIELDS.every((field) => {
    if (fresh?.[field] === undefined) return true;
    const before = prev?.[field];
    const after = fresh[field];
    if (Array.isArray(before) && Array.isArray(after)) {
      if (before.length !== after.length) return false;
      const sortedBefore = [...before].sort();
      const sortedAfter = [...after].sort();
      return sortedBefore.every((v, i) => v === sortedAfter[i]);
    }
    return before === after;
  });

const AuthProvider = ({ children }) => {
  const [userdetails, setUserDetails_] = useState(() => {
    try {
      const stored = localStorage.getItem("userdetails");
      return stored ? JSON.parse(stored) : null;
    } catch (err) {
      console.error("Failed to parse userdetails from localStorage:", err);
      localStorage.removeItem("userdetails");
      return null;
    }
  });

  /**
   * isAuthLoading — true during the very first render while React is
   * committing the initial hydrated state from localStorage.
   * ProtectedRoute renders nothing until false, preventing the race where
   * group_names is seen as empty before context propagates to consumers.
   */
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    setIsAuthLoading(false);
  }, []);

  // token is derived from userdetails — truthy when authenticated, null when not.
  // Kept in context so the ~100 components using `if (token && ...)` as their
  // data-fetch trigger continue to work without modification.
  // Never stored in localStorage — userdetails is the persistent auth indicator.
  const token = userdetails ? true : null;

  // no-op — call sites in Login/Logout/components are kept as-is
  const setToken = () => {};

  // Memoised: it lands in the context value and in the effect deps below, and a
  // fresh identity each render would re-run every consumer that depends on it.
  const setUserDetails = useCallback((ud) => setUserDetails_(ud), []);

  /**
   * Merge server-side permission fields into the cached details.
   *
   * Returns the previous object untouched when nothing moved. That matters:
   * roughly forty components read this context, and the effect below persists
   * it to localStorage on every change, so handing back a new object on each
   * tab focus would re-render all of them and rewrite storage for nothing.
   */
  const applyPermissionFields = useCallback((fresh) => {
    if (!fresh) return;
    setUserDetails_((prev) => {
      if (!prev) return prev;                       // signed out in the meantime
      if (samePermissions(prev, fresh)) return prev;
      const next = { ...prev };
      SYNCED_FIELDS.forEach((field) => {
        if (fresh[field] !== undefined) next[field] = fresh[field];
      });
      return next;
    });
  }, []);

  const lastSyncRef = useRef(0);

  /**
   * Re-read the caller's own permissions from the server.
   *
   * Until this existed, `userdetails` was written once at login and never
   * revalidated, so an admin's group change only reached the user after a sign
   * out and back in — the hard navigation in Login.jsx was the only thing that
   * ever refreshed it.
   */
  const syncPermissions = useCallback(async ({ force = false } = {}) => {
    // A signed-out visitor has nothing to refresh and would just draw a 401.
    if (!localStorage.getItem("userdetails")) return;

    const now = Date.now();
    if (!force && now - lastSyncRef.current < RESYNC_MIN_INTERVAL_MS) return;
    lastSyncRef.current = now;

    try {
      const { data } = await api.get("/auth/users/me/");
      applyPermissionFields(data);
    } catch {
      // Deliberately silent. A genuinely dead session is already torn down by
      // the 401 interceptor in api.js; anything else here is a transient (
      // offline, 5xx, a slow tenant) and must leave the cached permissions
      // standing rather than sign a working session out or blank its menus.
    }
  }, [applyPermissionFields]);

  useEffect(() => {
    syncPermissions({ force: true });

    const onMaybeVisible = () => {
      if (document.visibilityState === "visible") syncPermissions();
    };
    // Both, and they overlap on purpose: visibilitychange catches tab switches,
    // focus catches switching back from another application while the tab stays
    // "visible". The interval throttle absorbs the double fire.
    // NB visibilitychange is a document event, not a window one.
    document.addEventListener("visibilitychange", onMaybeVisible);
    window.addEventListener("focus", onMaybeVisible);
    return () => {
      document.removeEventListener("visibilitychange", onMaybeVisible);
      window.removeEventListener("focus", onMaybeVisible);
    };
  }, [syncPermissions]);

  useEffect(() => {
    // The silent token refresh runs inside an axios interceptor, outside React,
    // so it hands the payload over as a window event — the same bridge api.js
    // already uses to reach InstalledAppsProvider.
    const onRefreshed = (event) => applyPermissionFields(event.detail?.userdetails);
    window.addEventListener(USERDETAILS_EVENT, onRefreshed);
    return () => window.removeEventListener(USERDETAILS_EVENT, onRefreshed);
  }, [applyPermissionFields]);

  useEffect(() => {
    if (userdetails && typeof userdetails === 'object') {
      localStorage.setItem('userdetails', JSON.stringify(userdetails));
      // Formatters read the zone synchronously from storage (utils/locale.js).
      if (userdetails.effective_timezone) {
        localStorage.setItem('timezone', userdetails.effective_timezone);
      }
    } else {
      localStorage.removeItem('userdetails');
      localStorage.removeItem('timezone');
    }
  }, [userdetails]);

  const logout = useCallback(() => {
    setUserDetails_(null);
    // JWT cookies cleared server-side. Clean up all localStorage auth keys.
    localStorage.removeItem('token');     // stale DRF token from pre-migration sessions
    localStorage.removeItem('userdetails');
    localStorage.removeItem('tenant_schema');
    localStorage.removeItem('timezone');
    localStorage.removeItem('company_timezone');
    localStorage.removeItem('company_currency');
  }, []);

  const contextValue = useMemo(
    () => ({
      token,
      setToken,
      userdetails,
      setUserDetails,
      logout,
      isAuthLoading,
      syncPermissions,
    }),
    [token, userdetails, isAuthLoading, setUserDetails, logout, syncPermissions]
  );

  // Provide the authentication context to the children components
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
